import { Button, DrawerBody, DrawerFooter, Flex, Spinner, Text, useToast } from "@chakra-ui/react"
import { DrawerContext } from "components/drawer"
import TickList from "components/select/TickList"
import { AuthContext } from "contexts/AuthContext"
import { getAll, updateMany } from "lib/firebase"
import { planGroupLocations } from "lib/membership"
import { useContext, useEffect, useMemo, useState } from "react"

// The locations of one group, and a way to move more locations into it. A location has exactly one
// group, so there is no "remove": the locations that are in this group are ticked and locked, and
// ticking another one REPLACES its current group. Saving writes everything in ONE update, from
// data read again right before writing.
export default function GroupLocations({ group, onChange }) {
  const { user } = useContext(AuthContext)
  const { closeDrawer } = useContext(DrawerContext)
  const toast = useToast()

  const [state, setState] = useState({ loading: true, locations: [], groups: [], error: null })
  const [ticked, setTicked] = useState(new Set())
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let active = true
    Promise.all([getAll("location", user.clientId), getAll("group", user.clientId)])
      .then(([locations, groups]) => {
        if (!active) return
        setState({ loading: false, locations, groups, error: null })
        setTicked(new Set(locations.filter(l => l.groupId === group.id).map(l => l.id)))
      })
      .catch(err => active && setState({ loading: false, locations: [], groups: [], error: err.message || String(err) }))
    return () => { active = false }
  }, [user.clientId, group.id])

  const initial = useMemo(() => new Set(state.locations.filter(l => l.groupId === group.id).map(l => l.id)), [state.locations, group.id])

  const items = useMemo(() => {
    const nameOf = new Map(state.groups.map(g => [g.id, g.name]))
    return state.locations.map(location => {
      const inThis = location.groupId === group.id
      const other = nameOf.get(location.groupId) || "a deleted group"
      return {
        id: location.id,
        label: location.name || "",
        locked: inThis,
        hint: inThis ? "in this group" : `now in: ${other}`,
        tag: undefined,
      }
    })
  }, [state, group.id])

  const toMove = [...ticked].filter(id => !initial.has(id))

  const toggle = (id) => setTicked(current => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const setMany = (ids, checked) => setTicked(current => {
    const next = new Set(current)
    ids.forEach(id => checked ? next.add(id) : next.delete(id))
    return next
  })

  const save = async () => {
    if (saving || toMove.length === 0) return
    setSaving(true)
    try {
      const locations = await getAll("location", user.clientId)
      const plan = planGroupLocations({ locations, groupId: group.id, add: toMove })
      if (Object.keys(plan.updates).length > 0) {
        await updateMany(plan.updates)
      }
      onChange && onChange()
      closeDrawer()
      toast({ title: `${plan.moved.length} location(s) moved to ${group.name}`, status: "success", duration: null, isClosable: true })
    } catch (err) {
      toast({ title: "Error!", description: err.message || String(err), status: "error", duration: null, isClosable: true })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <DrawerBody>
        {state.loading ? (
          <Flex justify="center" pt={8}><Spinner /></Flex>
        ) : state.error ? (
          <Text color="red.500">{state.error}</Text>
        ) : (
          <>
            <Text fontSize="sm" color="gray.500" mb={2}>
              Tick the locations that should belong to this group. A ticked location leaves its current group and takes the one
              of this group, with its broadcast. A location always has a group, so the locations already in this group stay in it.
            </Text>
            <TickList items={items} initial={initial} ticked={ticked} saving={saving} onToggle={toggle} onSetMany={setMany} noun="locations" />
          </>
        )}
      </DrawerBody>
      <DrawerFooter>
        <Button colorScheme="brand" borderRadius="30px" me="10px" isLoading={saving} isDisabled={state.loading || !!state.error || toMove.length === 0} onClick={save}>
          Move{toMove.length > 0 ? ` (${toMove.length})` : ""}
        </Button>
      </DrawerFooter>
    </>
  )
}
